import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Branding from './Branding';

/**
 * ConnectionLoadingState - 2025 Apple/Google Minimal Design
 *
 * Clean, professional loading state for voice connections
 * Design principles: Minimalism, clarity, performance
 * Inspired by: iOS 18, Google Meet, FaceTime
 *
 * Features:
 * - Minimal circular spinner (thin, infinite rotate)
 * - Single clear message (no rotation)
 * - Real user avatar or letter circle
 * - Subtle fade-in animation
 * - Timeout handling
 */
const ConnectionLoadingState = ({
  state = 'idle', // 'idle', 'connecting', 'connected', 'disconnecting', 'error'
  userName = 'Mira',
  avatarUrl,
  onTimeout,
  onRetry
}) => {
  const [timeElapsed, setTimeElapsed] = useState(0);

  // Simple, clear messages (no rotation - reduces cognitive load)
  const getMessage = () => {
    switch (state) {
      case 'connecting':
        return "Connecting...";
      case 'disconnecting':
        return "Ending session...";
      case 'error':
        return "Connection failed";
      default:
        return "";
    }
  };

  const getSubMessage = () => {
    switch (state) {
      case 'connecting':
        // Rotate through messages during 20-second connect (like Google Meet/iOS)
        const connectMessages = [
          "Waking up Mira",
          "Loading your learning profile",
          "Preparing your session",
          "Almost ready"
        ];
        const connectIndex = Math.floor((timeElapsed / 5000) % connectMessages.length);
        return connectMessages[connectIndex];
      case 'disconnecting':
        // Rotate through messages during 20-second disconnect (like Google Meet/iOS)
        const disconnectMessages = [
          "Ending call with Mira",
          "Saving your progress",
          "Cleaning up the room",
          "See you next time"
        ];
        const messageIndex = Math.floor((timeElapsed / 5000) % disconnectMessages.length);
        return disconnectMessages[messageIndex];
      default:
        return "";
    }
  };

  const message = getMessage();
  const subMessage = getSubMessage();

  // Simple timer for timeout handling only
  useEffect(() => {
    if (state !== 'connecting' && state !== 'disconnecting') {
      setTimeElapsed(0);
      return;
    }

    const timer = setInterval(() => {
      setTimeElapsed(prev => prev + 100);
    }, 100);

    return () => clearInterval(timer);
  }, [state]);

  // Timeout handling
  useEffect(() => {
    if (!onTimeout) return;

    // Timeout after 45 seconds for connecting (cloud deployments may have cold starts)
    if (state === 'connecting' && timeElapsed > 45000) {
      onTimeout();
    }

    // Timeout after 20 seconds for disconnecting (allows full 15s cleanup)
    if (state === 'disconnecting' && timeElapsed > 20000) {
      onTimeout();
    }
  }, [timeElapsed, state, onTimeout]);

  // Get user initials for letter circle
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (state === 'idle' || state === 'connected') {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: Branding.spacing.md,
          padding: Branding.spacing.xl
        }}
      >
        {/* Avatar with minimal spinner overlay */}
        <div style={{ position: 'relative' }}>
          {/* Minimal circular spinner - Apple style */}
          {(state === 'connecting' || state === 'disconnecting') && (
            <svg
              width="100"
              height="100"
              style={{
                position: 'absolute',
                top: '-8px',
                left: '-8px'
              }}
            >
              {/* Thin spinner - infinite rotate */}
              <motion.circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke={state === 'error' ? '#E74C3C' : Branding.colors.slateGray}
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="290" // 2 * PI * 46
                strokeDashoffset="220"
                animate={{
                  rotate: 360
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{
                  transformOrigin: 'center'
                }}
              />
            </svg>
          )}

          {/* Avatar - clean and simple */}
          <div
            style={{
              width: '84px',
              height: '84px',
              borderRadius: '50%',
              background: avatarUrl ? 'transparent' : Branding.colors.darkTeal,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: Branding.typography.fontSize['2xl'],
              fontWeight: Branding.typography.fontWeight.semibold,
              color: Branding.colors.pureWhite,
              border: `3px solid ${Branding.colors.nearlyWhite}`,
              boxShadow: state === 'error'
                ? '0 4px 16px rgba(231, 76, 60, 0.2)'
                : '0 4px 16px rgba(0, 0, 0, 0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={userName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <span>{getInitials(userName)}</span>
            )}
          </div>
        </div>

        {/* Single, clear message - no rotation */}
        <div style={{ textAlign: 'center' }}>
          <p style={{
            fontSize: Branding.typography.fontSize.base,
            fontWeight: Branding.typography.fontWeight.medium,
            color: state === 'error' ? '#E74C3C' : Branding.colors.darkSlate,
            margin: 0
          }}>
            {message}
          </p>

          {/* Submessage for additional context */}
          {subMessage && (
            <p style={{
              fontSize: Branding.typography.fontSize.sm,
              fontWeight: Branding.typography.fontWeight.normal,
              color: Branding.colors.slateGray,
              margin: '8px 0 0 0'
            }}>
              {subMessage}
            </p>
          )}

          {/* Error retry button - iOS style */}
          {state === 'error' && onRetry && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              whileTap={{ scale: 0.96 }}
              onClick={onRetry}
              style={{
                marginTop: Branding.spacing.md,
                padding: `10px 24px`,
                background: Branding.colors.vibrantOrange,
                color: Branding.colors.pureWhite,
                border: 'none',
                borderRadius: '10px',
                fontSize: Branding.typography.fontSize.sm,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
            >
              Try Again
            </motion.button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConnectionLoadingState;
